import { RefObject, useMemo, lazy, useEffect, MutableRefObject, CSSProperties, useRef, useState } from 'react';
import type ReactQuill from 'react-quill-new';

import SSRQuillWrapper from './ssr-quill-wrapper.js';

const defaultModules = {};

interface Props {
    quillRef?: RefObject<ReactQuill>;
    resultRef?: MutableRefObject<AnyFunction>;
    defaultValue?: string;
    onChange?: (value: string, delta: any, source: string) => void;
    modules?: any;
    formats?: any;
    readOnly?: boolean;
    disableClicks?: boolean;
    onMouseMove?: (evt: any) => void;
    onMouseLeave?: (evt: any) => void;
    onClick?: (evt: any) => void;
    style?: CSSProperties;
}

const ReactQuillComponent = lazy(() => {
    if (typeof document !== 'undefined') {
        return import('react-quill-new');
    } else {
        return {
            __esModule: true,
            default: () => null,
        } as any;
    }
});

const QuillWrapper: FC<Props> = ({
    // eslint-disable-next-line @typescript-eslint/no-unused-vars
    children,
    quillRef,
    resultRef,
    defaultValue = '',
    readOnly,
    modules = {},
    disableClicks = false,
    onClick,
    onMouseLeave,
    onMouseMove,
    style,
    ...props
}) => {
    const [render, setRender] = useState(false);
    const _quillRef = useRef<ReactQuill>();
    const ref = quillRef ?? _quillRef;
    const processedDefault = readOnly
        ? // Change play link class to indicate this is a read-only version of the link
          defaultValue?.replace(
              /<a class="play-link-pill" href="(.+?)">/g,
              '<a class="play-link-pill read-only" href="$1">'
          )
        : // force ending space when the provided value ends with a space
          defaultValue?.replace(/&nbsp;/g, ' ').replace(' </p>', '&nbsp;</p>');

    useEffect(() => {
        if (resultRef) {
            // eslint-disable-next-line no-param-reassign
            resultRef.current = () => {
                if (ref.current) {
                    const quill = ref.current.makeUnprivilegedEditor(ref.current.getEditor());

                    if (quill.getLength() > 1) {
                        return quill.getHTML();
                    }
                }

                return '';
            };
        }
    }, [resultRef, ref?.current]);

    useEffect(() => {
        setRender(true);
    }, []);

    const classNames = ['quill-wrapper'];

    if (readOnly) classNames.push('readonly');

    // If the modules or formats change on every render, the entire Quill editor will re-render and cause issues
    const totalModules = useMemo(() => {
        return {
            ...defaultModules,
            ...modules,
        };
    }, [modules]);

    const quill = render ? (
        <ReactQuillComponent
            key={readOnly ? processedDefault : undefined}
            ref={ref}
            defaultValue={processedDefault}
            readOnly={readOnly}
            modules={totalModules}
            {...props}
        />
    ) : (
        <SSRQuillWrapper value={processedDefault} />
    );

    return (
        <div
            className={classNames.join(' ')}
            onClick={readOnly && !disableClicks ? onClick : null}
            onMouseMove={onMouseMove}
            onMouseLeave={onMouseLeave}
            style={style}
        >
            {quill}
        </div>
    );
};

export default QuillWrapper;
